import { useCallback, useEffect, useMemo, useState } from 'react';
import { HeapCaptureState, HeapDetectionFinishedEvent, HeapFrameCapturesUploadedEvent } from '../components/Heap/types';
import { heapRecordingFilterOptionsKeys } from './queries/heapRecordingFilterOptions';
import { heapRecordingKeys } from './queries/heapRecordingList';
import { queryClient } from './queryClient';
import { useWebSocket } from './useWebSocket';

export function useHeapEvents(jobsiteId: string, triggerId: string | undefined, token?: string) {
  const [cameraConnected, setCameraConnected] = useState<boolean>(false);
  const [captureState, setCaptureState] = useState<HeapCaptureState>({ status: 'idle' });

  function handleWsConnect() {
    setCameraConnected(true);
  }
  function handleWsDisconnect() {
    setCameraConnected(false);
  }
  function handleWsError(error: Error) {
    console.error('WebSocket error:', error);
    setCameraConnected(false);
  }

  const handleWsMessage = useCallback(
    (data: string) => {
      try {
        const event = JSON.parse(data);
        switch (event.type) {
          case 'frame_captures_uploaded': {
            const payload = (event as HeapFrameCapturesUploadedEvent).payload;
            if (payload?.trigger_id === triggerId) {
              setCaptureState({
                status: 'uploaded',
                capturedAt: payload.recorded_at ? new Date(payload.recorded_at) : undefined,
                triggerId: payload.trigger_id,
              });
            }
            break;
          }
          case 'detection_finished': {
            const payload = (event as HeapDetectionFinishedEvent).payload;
            if (payload?.trigger_id === triggerId) {
              setCaptureState({
                status: 'processed',
                capturedAt: payload.recorded_at ? new Date(payload.recorded_at) : undefined,
                triggerId: payload.trigger_id,
                bucket: payload.bucket_name,
                object: payload.object_name,
              });
            }
            // Invalidate heap recording list to fetch the new recording
            queryClient.invalidateQueries({ queryKey: heapRecordingKeys.base() });
            queryClient.invalidateQueries({ queryKey: heapRecordingFilterOptionsKeys.base() });
            break;
          }
          default:
            console.warn('Unknown event type:', event.type);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    },
    [triggerId]
  );
  const url = window.location.origin.replace(/^http/, 'ws');
  const path = '/api/heap-live.socket';
  const query = useMemo(() => ({ jobsiteId: jobsiteId || '' }), [jobsiteId]);
  const auth = useMemo(() => (token ? { token } : undefined), [token]);

  const { readyState, connect } = useWebSocket(url, {
    path,
    auth,
    query,
    autoConnect: false,
    onConnect: handleWsConnect,
    onDisconnect: handleWsDisconnect,
    onError: handleWsError,
    onEvent: handleWsMessage,
  });

  useEffect(() => {
    if (token && readyState === 0) {
      connect();
    }
  }, [token, connect, readyState]);

  return { cameraConnected, captureState, setCaptureState };
}
